import * as React from 'react';

const NUM_FREE_MARKETING_LICENSES = 3;

export function getErrorText(
    numEnabledUsers: number,
    numUsers: string,
    isTrial: boolean,
    numMarketingLicenses?: number | null
) {
    const numSelectedUsers = Number.parseInt(numUsers);
    if (numSelectedUsers < 1) {
        return 'You must purchase at least 1 user account.';
    }

    const isBelowUserLimit = numSelectedUsers < numEnabledUsers;
    if (isBelowUserLimit) {
        return (
            <>
                You must purchase at least {numEnabledUsers} user accounts.
                <br />
                If you’d like to reduce your current user count,
                <br />
                please remove some users first.
            </>
        );
    }

    // We only want to gate the +3 marketing licenses rule for trials, as that
    // is the only state where users can add an unlimited amount of marketing
    // only users
    if (!isTrial || !numMarketingLicenses) {
        return null;
    }

    // If the user is on a trial, they need to purchase enough crm seats to
    // support the number of marketing licenses they currently have assigned
    // (Upon purchase, you get [numCrmLicenses + 3] Marketing licenses). If they
    // have more marketing seats than allowed, warn them with an error stating
    // how many CRM seats they need to purchase.
    if (numSelectedUsers < numMarketingLicenses - NUM_FREE_MARKETING_LICENSES) {
        return (
            <>
                You need to purchase at least {numMarketingLicenses - NUM_FREE_MARKETING_LICENSES}{' '}
                sales seats
            </>
        );
    }

    return null;
}

export function getUserCountText(numEnabledUsers: number, numMarketingUsers?: number | null) {
    if (numMarketingUsers && numMarketingUsers > 0) {
        return `You have ${numEnabledUsers} sales
        and ${numMarketingUsers} marketing license${numMarketingUsers === 1 ? '' : 's'}`;
    }

    return `You have ${numEnabledUsers} sales user${numEnabledUsers === 1 ? '' : 's'}`;
}

export function getEngagementUserCountText(numEnabledLicenses?: number | null) {
    return `You have ${numEnabledLicenses || 0} active engagement license${
        numEnabledLicenses === 1 ? '' : 's'
    }`;
}

export function getEngagementErrorText(
    numEnabledLicenses: number | null | undefined,
    numUsers: string
) {
    const numSelectedUsers = Number.parseInt(numUsers);
    if (numSelectedUsers < 1) {
        return 'You must purchase at least one engagement license';
    }

    const isBelowUserLimit = numSelectedUsers < (numEnabledLicenses || 0);
    if (isBelowUserLimit) {
        return `You must purchase at least ${
            numEnabledLicenses || 0
        } engagement licenses or remove active licenses to continue.`;
    }

    return null;
}

export function getDisabledCyclePopoverText(suite: 'sales' | 'marketing' | 'engagement') {
    if (suite === 'marketing') {
        return 'Marketing plans are available as a monthly subscription only.';
    }

    if (suite === 'engagement') {
        return 'Engagement plans are available as a monthly subscription only.';
    }

    return null;
}

export function getDisabledUserCountText(suite: 'sales' | 'marketing' | 'engagement'): string {
    if (suite === 'marketing') {
        return 'Marketing plans automatically get users equal to the number of sales seats you have + 3.';
    }

    if (suite === 'engagement') {
        return 'Engagement foundation plans automatically get users equal to the sales seats you have.';
    }

    return '';
}

export function getDisabledUserCount(
    suite: 'sales' | 'marketing' | 'engagement',
    numSalesLicenses: number
): string {
    if (suite === 'marketing') {
        return String(numSalesLicenses + NUM_FREE_MARKETING_LICENSES);
    } else if (suite === 'engagement') {
        return String(numSalesLicenses);
    }

    return '';
}
